// pages/ReservasPage.jsx
import React, { useEffect, useState, useRef } from "react";
import { getReservas, createReserva, updateReserva, deleteReserva } from "../api/reservas.js";
import { getClientes } from "../api/clientes.js";
import { getMesas } from "../api/mesas.js";
import InputGroup from "../components/InputGroup";
import SortableHeader from "../components/SortableHeader";
import { useSortableData } from "../hooks/useSortableData.js";
import { usePagination } from "../hooks/usePagination.js";
import { useAuth } from "../context/AuthContext.js";
import Pagination from "../components/Pagination";

const ReservasPage = () => {
    const { usuario: usuarioLogueado } = useAuth();
    const [reservas, setReservas] = useState([]);
    const [filteredReservas, setFilteredReservas] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [clientes, setClientes] = useState([]);
    const [mesas, setMesas] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const inputRefs = useRef({});

    const [reserva, setReserva] = useState({
        fecha_reserva: "",
        hora_reserva: "",
        numero_personas: "",
        estado: "pendiente",
        observaciones: "",
        id_cliente: "",
        id_mesa: "",
    });

    const { sortedItems: sortedReservas, sortConfig, setSortConfig } = useSortableData(filteredReservas);
    // Hook de paginación
    const { currentPage, setCurrentPage, paginatedItems: currentReservas, totalPages, itemsPerPage } = usePagination(sortedReservas, 20);

    // 🔹 Cargar clientes
    useEffect(() => {
        const loadClientes = async () => {
        try {
            const data = await getClientes();
            setClientes(Array.isArray(data) ? data : data.clientes || []);
        } catch (err) {
            console.error("Error al cargar clientes:", err);
        }
        };
        loadClientes();
    }, []);

    // 🔹 Cargar mesas
    useEffect(() => {
        const loadMesas = async () => {
        try {
            const data = await getMesas();
            setMesas(Array.isArray(data) ? data : data.mesas || []);
        } catch (err) {
            console.error("Error al cargar mesas:", err);
        }
        };
        loadMesas();
    }, []);

    // 🔹 Cargar reservas
    const loadReservas = async () => {
        try {
        const data = await getReservas();
        const lista = Array.isArray(data) ? data : data.reservas || [];
        setReservas(lista);
        setFilteredReservas(lista);
        } catch (err) {
        console.error("Error al cargar reservas:", err);
        }
    };

    useEffect(() => {
        loadReservas();
    }, []);

    // 🔍 Filtro de búsqueda
    useEffect(() => {
        const filtered = reservas.filter((r) =>
        Object.values(r).some((val) =>
            String(val).toLowerCase().includes(searchTerm.toLowerCase())
        )
        );
        setFilteredReservas(filtered);
    }, [searchTerm, reservas]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setReserva({ ...reserva, [name]: value });
    };

    const resetForm = () => {
        setReserva({ fecha_reserva: "", hora_reserva: "", numero_personas: "", estado: "pendiente", observaciones: "", id_cliente: "", id_mesa: "", });
        setErrors({});
        setEditMode(false);
    };

    const validateReserva = (data) => {
        const newErrors = {};
        if (!data.fecha_reserva) newErrors.fecha_reserva = "Ingrese la fecha de la reserva.";
        if (!data.hora_reserva) newErrors.hora_reserva = "Ingrese la hora de la reserva.";
        if (!data.numero_personas || isNaN(data.numero_personas) || data.numero_personas <= 0)
        newErrors.numero_personas = "Ingrese un número de personas válido.";
        if (!data.id_cliente) newErrors.id_cliente = "Seleccione un cliente.";
        if (!data.id_mesa) newErrors.id_mesa = "Seleccione una mesa.";
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validateReserva(reserva);
        if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        const firstErrorField = Object.keys(validationErrors)[0];
        const fieldRef = inputRefs.current[firstErrorField];
        if (fieldRef?.scrollIntoView) {
            fieldRef.scrollIntoView({ behavior: "smooth", block: "center" });
            fieldRef.focus();
        }
        return;
        }

        try {
        if (editMode) {
            await updateReserva(reserva.id_reserva, reserva);
            alert("Reserva actualizada correctamente");
        } else {
            await createReserva(reserva);
            alert("Reserva creada correctamente");
        }
        resetForm();
        setModalOpen(false);
        loadReservas();
        } catch (err) {
        console.error("Error del backend:", err.response?.data || err.message);
        const backendMsg =
            err.response?.data?.error ||
            err.response?.data?.message ||
            "Error al guardar la reserva.";
        setErrors({ general: backendMsg });
        alert(backendMsg);
        }
    };

    const handleEdit = (r) => {
        setReserva(r);
        setEditMode(true);
        setModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Seguro que deseas eliminar esta reserva?")) {
        await deleteReserva(id);
        loadReservas();
        }
    };

    return (
        <div className="p-6 max-w-[90rem] mx-auto">
        <h1 className="text-3xl font-bold mb-6 text-center">Gestión de Reservas</h1>

        {/* 🔍 Buscador + Botón */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <input type="text" placeholder="🔍 Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="border p-2 rounded w-full md:w-1/2" />
            <button onClick={() => { resetForm(); setModalOpen(true); }} className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700" > ➕ Nueva Reserva </button>
        </div>

        {/* 🧾 Tabla de Reservas */}
        <div className="overflow-x-auto bg-white shadow-md rounded-xl w-full">
            <table className="min-w-full text-sm text-left divide-y divide-gray-200">
            <thead className="bg-gray-100 text-gray-700 font-semibold">
                <tr>
                <SortableHeader label="N°" sortKey="id_reserva" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                {usuarioLogueado?.isSuperAdmin && (
                    <SortableHeader label="Restaurante" sortKey="mesa.restaurante.nombre_comercial" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                )}
                <SortableHeader label="Fecha" sortKey="fecha_reserva" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                <SortableHeader label="Hora" sortKey="hora_reserva" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                <SortableHeader label="Personas" sortKey="numero_personas" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                <SortableHeader label="Estado" sortKey="estado" sortConfig={sortConfig} setSortConfig={setSortConfig} className="px-4 py-2" />
                <th className="px-4 py-2">Cliente</th>
                <th className="px-4 py-2">Mesa</th>
                <th className="px-4 py-2 text-center">Acciones</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
                {currentReservas.length > 0 ? (
                currentReservas.map((r, idx) => (
                    <tr key={r.id_reserva} className="hover:bg-gray-50">
                    <td className="px-4 py-2">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                    {usuarioLogueado?.isSuperAdmin && (
                        <td className="px-4 py-2">
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                {r.mesa?.restaurante?.nombre_comercial || "Sin restaurante"}
                            </span>
                        </td>
                    )}
                    <td className="px-4 py-2">{r.fecha_reserva}</td>
                    <td className="px-4 py-2">{r.hora_reserva}</td>
                    <td className="px-4 py-2">{r.numero_personas}</td>
                    <td className="px-4 py-2 capitalize">{r.estado}</td>
                    <td className="px-4 py-2">{r.cliente?.nombre || "—"}</td>
                    <td className="px-4 py-2">Mesa {r.mesa?.numero_mesa || "—"}</td>
                    <td className="px-4 py-2 text-center flex justify-center gap-2">
                        <button className="bg-yellow-400 text-white px-3 py-1 rounded hover:bg-yellow-500" onClick={() => handleEdit(r)} > Editar </button>
                        <button className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600" onClick={() => handleDelete(r.id_reserva)} > Eliminar </button>
                    </td>
                    </tr>
                ))
                ) : (
                <tr>
                    <td colSpan={usuarioLogueado?.isSuperAdmin ? "9" : "8"} className="text-center py-4 text-gray-500"> No hay reservas registradas </td>
                </tr>
                )}
            </tbody>
            </table>
        </div>

        {/* Paginación */}
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

        {/* 🧩 Modal Crear / Editar */}
        {modalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-lg w-[70%] max-h-[90vh] overflow-y-auto px-10 py-6 relative">
                <button onClick={() => setModalOpen(false)} className="absolute top-2 right-3 text-gray-600 hover:text-red-500 text-xl" > ✖ </button>
                <h2 className="text-2xl font-semibold mb-6 text-center"> {editMode ? "Editar Reserva" : "Nueva Reserva"} </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputGroup label="Fecha" error={errors.fecha_reserva}> <input type="date" name="fecha_reserva" value={reserva.fecha_reserva} onChange={handleChange} className="input-base" ref={(el) => (inputRefs.current["fecha_reserva"] = el)} /> </InputGroup>
                    <InputGroup label="Hora" error={errors.hora_reserva}> <input type="time" name="hora_reserva" value={reserva.hora_reserva} onChange={handleChange} className="input-base" ref={(el) => (inputRefs.current["hora_reserva"] = el)} /> </InputGroup>
                    <InputGroup label="N° Personas" error={errors.numero_personas}> <input name="numero_personas" value={reserva.numero_personas} onChange={handleChange} placeholder="Ej: 4" className="input-base" ref={(el) => (inputRefs.current["numero_personas"] = el)} /> </InputGroup>
                    <InputGroup label="Estado" error={errors.estado}> <select name="estado" value={reserva.estado} onChange={handleChange} className="input-base" > <option value="pendiente">Pendiente</option> <option value="confirmada">Confirmada</option> <option value="cancelada">Cancelada</option> </select> </InputGroup>
                    <InputGroup label="Cliente" error={errors.id_cliente}>
                    <select name="id_cliente" value={reserva.id_cliente} onChange={handleChange} className="input-base" > <option value="">Seleccione un cliente</option> {clientes.map((c) => ( <option key={c.id_cliente} value={c.id_cliente}> {c.nombre} </option> ))} </select>
                    </InputGroup>
                    <InputGroup label="Mesa" error={errors.id_mesa}>
                    <select name="id_mesa" value={reserva.id_mesa} onChange={handleChange} className="input-base" > <option value="">Seleccione una mesa</option> {mesas.map((m) => ( <option key={m.id_mesa} value={m.id_mesa}> Mesa {m.numero_mesa} - Capacidad {m.capacidad} </option> ))} </select>
                    </InputGroup>
                    <InputGroup label="Observaciones"> <textarea name="observaciones" value={reserva.observaciones} onChange={handleChange} placeholder="Detalles adicionales..." className="input-base" /> </InputGroup>
                </div>
                {errors.general && (
                    <p className="text-red-600 text-sm text-center">{errors.general}</p>
                )}
                <div className="text-center">
                    <button type="submit" className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700" > {editMode ? "Actualizar" : "Guardar"} </button>
                </div>
                </form>
            </div>
            </div>
        )}
        </div>
    );
};

export default ReservasPage;
